import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Menu,
  Users,
  RefreshCw,
  Edit,
  CreditCard,
  X,
  CalendarX,
  Ban,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { useHistory } from "react-router-dom";
import { Modal, DatePicker, Button, Space, message } from "antd";
import type { DatePickerProps } from "antd";
import moment from "moment";
import Colors from "@/config/Colors";
import { ImageWithFallback } from "./companyImageWithFallBack";
import {
  useEditCompany,
  useCompanyRenewal,
  useScheduleCompanyCancel,
  useCancelCompanyImmediately,
  useAddCompanyMembers,
  useDeactivateCompany,
} from "@/hooks/useCompanies";
import EditCompany from "@/components/Forms/EditCompany";
import NewCompanyUsers from "@/components/Forms/NewCompanyUsersForm";
import RenewCompanySubscriptions from "@/components/Forms/RenewCompanySubscriptions";
import GenericDialog from "@/components/Dialog/GenericDialog";
import { CustomIcons } from "@/assets";
import { modelTitle } from "@/styles/ModalStyles";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

interface CompanyCardProps {
  companyData?: {
    id: number;
    companyName: string;
    imgSrc: string;
    companyLogo?: string;
    corporateAgreement: number;
    referralCode: string;
    communityCode: string;
    turnover: number;
    pricePerMonth: string;
    whitelabel?: IWhitelabelInfo;
    activePremiumUsers?: Array<{
      id: number;
      name: string;
      email: string;
      avatar: string;
      plan: string;
    }>;
  };
  onUpdate?: () => void;
}

export function CompanyCardV2({ companyData, onUpdate }: CompanyCardProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openEditCompanyModal, setOpenEditCompanyModal] = useState(false);
  const [openNewCompanyUsersModal, setOpenNewCompanyUsersModal] =
    useState(false);
  const [openImmediateCancelModal, setOpenImmediateCancelModal] =
    useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openRenewModal, setOpenRenewModal] = useState(false);
  const [openDeactivateModal, setOpenDeactivateModal] = useState(false);

  const navigate = useHistory();
  const editCompanyHook = useEditCompany();
  const renewCompanyUsers = useCompanyRenewal();
  const cancelImmediately = useCancelCompanyImmediately();
  const scheduleCancel = useScheduleCompanyCancel();
  const addCompanyMembers = useAddCompanyMembers();
  const deactivateCompany = useDeactivateCompany();
  const [messageApi, contextHolder] = message.useMessage();

  let cancelDate: string;
  let scheduleVars: IScheduleCancelParams;

  // Return early if no data
  if (!companyData) {
    return (
      <Card className="w-full max-w-2xl mx-auto">
        <CardContent className="p-6">
          <p className="text-muted-foreground text-center">
            No company data available
          </p>
        </CardContent>
      </Card>
    );
  }

  const data = companyData;

  const navigateToMembers = (companyId: number | string) => {
    navigate.push(`/home/company-members/${companyId}`);
  };

  const navigateToPayment = (companyId: number | string) => {
    navigate.push(`/home/proof-of-payment/${companyId}`);
  };

  const onChangeDate: DatePickerProps["onChange"] = (date) => {
    cancelDate = moment(date?.toString()).format("YYYY/MM/DD");
    scheduleVars = {
      companyId: data.id.toString(),
      cancelDate,
    };
  };

  const handleEditCompany = async (company: IEditCompany) => {
    try {
      await editCompanyHook.mutateAsync(company);
      onUpdate?.();
      message.success("Changes Saved Successfully.");
      setOpenEditCompanyModal(false);
    } catch (error) {
      message.error("Failed to edit company");
    }
  };

  const handleCompanyRenewal = async (companyRenewal: ICompanyUserRenewal) => {
    try {
      companyRenewal.companyId = data.id;
      await renewCompanyUsers.mutateAsync(companyRenewal);
      message.success(
        "Subscription renewal will take up to 30 minutes to complete"
      );
      setOpenRenewModal(false);
    } catch (error) {
      message.error("Failed to renew company subscription");
    }
  };

  const handleScheduleCancelCompany = async () => {
    try {
      messageApi.open({
        key: "scheduleCancel",
        type: "loading",
        content: "Scheduling Cancel..",
        duration: 0,
      });
      setOpenCancelModal(false);
      await scheduleCancel.mutateAsync(scheduleVars);
      messageApi.open({
        key: "scheduleCancel",
        type: "success",
        content: "Subscription Cancel Scheduled..",
        duration: 2,
      });
    } catch (error) {
      messageApi.destroy("scheduleCancel");
    }
  };

  const handleImmediateCancelCompany = async () => {
    try {
      messageApi.open({
        key: "cancelImmediately",
        type: "loading",
        content: "Cancelling Subscription..",
        duration: 0,
      });
      setOpenImmediateCancelModal(false);
      await cancelImmediately.mutateAsync(data.id.toString());
      messageApi.open({
        key: "cancelImmediately",
        type: "success",
        content: "Subscription Cancelled..",
        duration: 2,
      });
    } catch (error) {
      messageApi.destroy("cancelImmediately");
    }
  };

  const handleAddCompanyMembers = async (
    newCompanyMembers: ICompanyMembers
  ) => {
    try {
      newCompanyMembers.id = data.id;
      await addCompanyMembers.mutateAsync(newCompanyMembers);
      message.success(
        "Company Users will take 30 mins to be added successfully."
      );
      setOpenNewCompanyUsersModal(false);
    } catch (error: any) {
      // Surface the API's reason: plain-string 400s from the upload guard,
      // ProblemDetails (title / errors dictionary) from model validation.
      const data = error?.response?.data;
      const detail =
        typeof data === "string" && data
          ? data
          : data?.title ||
            (data?.errors && Object.values(data.errors).flat().join(", ")) ||
            error?.message;
      message.error(
        detail
          ? `Failed to add company members: ${detail}`
          : "Failed to add company members"
      );
    }
  };

  const handleDeactivateCompany = async () => {
    try {
      messageApi.open({
        key: "deactivateCompany",
        type: "loading",
        content: "Deactivating Company...",
        duration: 0,
      });
      setOpenDeactivateModal(false);
      await deactivateCompany.mutateAsync(data.id.toString());
      messageApi.open({
        key: "deactivateCompany",
        type: "success",
        content: "Company Deactivated Successfully",
        duration: 2,
      });
      onUpdate?.();
    } catch (error) {
      messageApi.destroy("deactivateCompany");
      messageApi.error("Failed to deactivate company");
    }
  };

  const handleClose = () => {
    setOpenEditCompanyModal(false);
    setOpenNewCompanyUsersModal(false);
    setOpenCancelModal(false);
    setOpenImmediateCancelModal(false);
    setOpenRenewModal(false);
    setOpenDeactivateModal(false);
    editCompanyHook.reset();
    addCompanyMembers.reset();
  };

  const menuOptions = [
    { label: "Add new members", icon: Users, action: "add-new-members" },
    { label: "View Members", icon: Users, action: "view-members" },
    { label: "Renew for all users", icon: RefreshCw, action: "renew-all" },
    { label: "Edit Company", icon: Edit, action: "edit-company" },
    {
      label: "View Payment history",
      icon: CreditCard,
      action: "payment-history",
    },
    { label: "Cancel Immediately", icon: X, action: "cancel-immediately" },
    {
      label: "Cancel on Next Billing Date",
      icon: CalendarX,
      action: "cancel-next-billing",
    },
    { label: "Deactivate Company", icon: Ban, action: "deactivate-company" },
  ];

  const handleMenuClick = (action: string) => {
    setIsMenuOpen(false);
    switch (action) {
      case "add-new-members":
        setOpenNewCompanyUsersModal(true);
        break;
      case "view-members":
        navigateToMembers(data.id);
        break;
      case "renew-all":
        setOpenRenewModal(true);
        break;
      case "edit-company":
        setOpenEditCompanyModal(true);
        break;
      case "payment-history":
        navigateToPayment(data.id);
        break;
      case "cancel-immediately":
        setOpenImmediateCancelModal(true);
        break;
      case "cancel-next-billing":
        setOpenCancelModal(true);
        break;
      case "deactivate-company":
        setOpenDeactivateModal(true);
        break;
      default:
        break;
    }
  };

  const editCompany: IEditCompany = {
    companyId: data.id,
    companyName: data.companyName,
    companyImage: data.companyLogo || "",
    communityCode: data.communityCode,
    referralCode: data.referralCode,
  };

  return (
    <>
      {contextHolder}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <Card className="w-full max-w-2xl mx-auto hover:shadow-lg transition-shadow duration-300 relative">
          <div className="absolute top-4 right-4 z-10">
            <motion.button
              className="p-2 hover:bg-muted rounded-md transition-colors flex justify-center items-center"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.4 }}
            >
              <Menu className="w-8 h-8" />
            </motion.button>

            <AnimatePresence>
              {isMenuOpen && (
                <motion.div
                  className="absolute top-12 right-0 bg-white border rounded-lg shadow-lg min-w-48 py-2"
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  {menuOptions.map((option, index) => {
                    const IconComponent = option.icon;
                    return (
                      <motion.button
                        key={option.action}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center gap-3 transition-colors"
                        onClick={() => handleMenuClick(option.action)}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.1, delay: index * 0.05 }}
                        whileHover={{ backgroundColor: "#f3f4f6" }}
                      >
                        <IconComponent className="w-4 h-4 text-gray-500" />
                        <span className="text-sm">{option.label}</span>
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <CardHeader className="px-4 pt-4 pb-2 pr-16">
            <div className="flex items-center gap-4">
              <motion.div
                className="w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <ImageWithFallback
                  src={data.companyLogo || data.imgSrc}
                  alt={`${data.companyName} logo`}
                  className="w-full h-full object-cover"
                />
              </motion.div>
              <motion.div
                className="flex-1 min-w-0"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <CardTitle
                  className="mb-1 truncate cursor-default"
                  title={data.companyName}
                >
                  {data.companyName.length > 25
                    ? `${data.companyName.substring(0, 25)}...`
                    : data.companyName}
                </CardTitle>
              </motion.div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-4">
            {/* User Statistics Section */}
            <motion.div
              className="flex justify-between gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              {[
                {
                  label: "Corporate Agreement",
                  value: data.corporateAgreement,
                },
                { label: "Turnover", value: data.turnover },
              ].map((item, index) => {
                const getBackgroundColor = () => {
                  if (item.label === "Corporate Agreement") {
                    return Colors.secondaryColor;
                  }
                  if (item.label === "Turnover") {
                    return Colors.backgroundSuccessColor;
                  }
                  return "transparent";
                };

                return (
                  <motion.div
                    key={item.label}
                    className="space-y-1 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer shadow-md text-center"
                    style={{ backgroundColor: getBackgroundColor() }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.6 + index * 0.1 }}
                    whileHover={{ scale: 1.02 }}
                  >
                    <label className="text-sm text-foreground block">
                      {item.label}
                    </label>
                    <div className="flex justify-center items-center gap-2">
                      <motion.span
                        className="text-md font-semibold"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3, delay: 0.8 + index * 0.1 }}
                      >
                        {item.value}
                        <span className="text-muted-foreground text-xs">
                          {` Member(s)`}
                        </span>
                      </motion.span>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Codes Section */}
            <motion.div
              className="flex justify-between gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.7 }}
            >
              {[
                { label: "Referral Code", value: data.referralCode },
                { label: "Community Code", value: data.communityCode },
              ].map((item, index) => (
                <motion.div
                  key={item.label}
                  className="space-y-2"
                  initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 1.8 + index * 0.1 }}
                >
                  <label className="text-muted-foreground">{item.label}</label>
                  <motion.div
                    className="p-3 bg-muted rounded-md hover:bg-muted/80 transition-colors cursor-pointer"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="font-mono">{item.value}</span>
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              className="border-t border-border"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.5, delay: 2.0 }}
            />

            {/* Pricing Section */}
            <motion.div
              className="flex justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 2.1 }}
            >
              <motion.div
                className="space-y-2 p-3 rounded-lg hover:bg-muted/50 transition-colors text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 2.2 }}
                whileHover={{ scale: 1.02 }}
              >
                <label className="text-muted-foreground">
                  Total Amount Due Monthly
                </label>
                <div className="flex items-center justify-center">
                  <motion.span
                    className="text-2xl"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 2.3 }}
                  >
                    {data.pricePerMonth}
                  </motion.span>
                </div>
              </motion.div>
            </motion.div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Add New Company Users Modal */}
      <Modal
        centered
        open={openNewCompanyUsersModal}
        onOk={() => setOpenNewCompanyUsersModal(false)}
        onCancel={() => setOpenNewCompanyUsersModal(false)}
        width={550}
        closeIcon={
          <CustomIcons.CloseIcon
            width={30}
            height={30}
            viewBox={null}
            svgColor="#1C274C"
          />
        }
        footer={null}
      >
        <div
          className="flex w-12 py-3 justify-content-start"
          style={modelTitle}
        >
          Add company users
        </div>
        <NewCompanyUsers
          onModalClose={handleClose}
          onFormFinish={handleAddCompanyMembers}
          isLoading={addCompanyMembers.isPending}
          isSuccess={addCompanyMembers.isSuccess}
        />
      </Modal>

      {/* Edit Company Drawer */}
      <Drawer
        open={openEditCompanyModal}
        onOpenChange={setOpenEditCompanyModal}
      >
        <DrawerContent className="w-1/2 max-w-none">
          <DrawerHeader>
            <DrawerTitle className="text-2xl font-bold">
              Edit Company
            </DrawerTitle>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto px-4">
            <EditCompany
              editCompanyValues={editCompany}
              onFormFinish={handleEditCompany}
              onModalClose={handleClose}
              isLoading={editCompanyHook.isPending}
              isSuccess={editCompanyHook.isSuccess}
            />
          </div>
        </DrawerContent>
      </Drawer>

      {/* Schedule Cancel Modal */}
      <Modal
        centered
        open={openCancelModal}
        onOk={() => setOpenCancelModal(false)}
        onCancel={() => setOpenCancelModal(false)}
        width={550}
        closeIcon={
          <CustomIcons.CloseIcon
            width={30}
            height={30}
            viewBox={null}
            svgColor="#1C274C"
          />
        }
        footer={null}
      >
        <div
          className="flex w-12 py-3 justify-content-start"
          style={modelTitle}
        >
          Schedule Cancellation
        </div>
        <Space direction="vertical" className="w-full mb-3">
          <DatePicker onChange={onChangeDate} className="w-1/2" />
        </Space>
        <div className="flex w-full gap-2 col-12 justify-content-end align-content-center">
          <Button
            type="primary"
            ghost
            onClick={handleClose}
            size="large"
            className="w-3"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            className="w-3 bg-[#1757a2] shadow-none"
            onClick={handleScheduleCancelCompany}
          >
            Save
          </Button>
        </div>
      </Modal>

      {/* Immediate Cancel Modal */}
      <Modal
        centered
        open={openImmediateCancelModal}
        onOk={() => setOpenImmediateCancelModal(false)}
        onCancel={() => setOpenImmediateCancelModal(false)}
        width={550}
        closeIcon={
          <CustomIcons.CloseIcon
            width={30}
            height={30}
            viewBox={null}
            svgColor="#1C274C"
          />
        }
        footer={null}
      >
        <GenericDialog
          onClose={() => setOpenImmediateCancelModal(false)}
          onConfirm={handleImmediateCancelCompany}
          text={`immediately cancel the subscription for ${data.companyName}`}
        />
      </Modal>

      {/* Renew Subscription Modal */}
      <Modal
        centered
        open={openRenewModal}
        onOk={() => setOpenRenewModal(false)}
        onCancel={() => setOpenRenewModal(false)}
        width={550}
        closeIcon={
          <CustomIcons.CloseIcon
            width={30}
            height={30}
            viewBox={null}
            svgColor="#1C274C"
          />
        }
        footer={null}
      >
        <div
          className="flex w-12 py-3 justify-content-start"
          style={modelTitle}
        >
          Renew Subscription
        </div>
        <RenewCompanySubscriptions
          onFormFinish={handleCompanyRenewal}
          onModalClose={handleClose}
          isLoading={renewCompanyUsers.isPending}
          isSuccess={renewCompanyUsers.isSuccess}
        />
      </Modal>

      {/* Deactivate Company Modal */}
      <Modal
        centered
        open={openDeactivateModal}
        onOk={() => setOpenDeactivateModal(false)}
        onCancel={() => setOpenDeactivateModal(false)}
        width={550}
        closeIcon={
          <CustomIcons.CloseIcon
            width={30}
            height={30}
            viewBox={null}
            svgColor="#1C274C"
          />
        }
        footer={null}
      >
        <GenericDialog
          onClose={() => setOpenDeactivateModal(false)}
          onConfirm={handleDeactivateCompany}
          text={`deactivate ${data.companyName}`}
        />
      </Modal>
    </>
  );
}
